import { Button, Textarea, ToastMessage } from '@cokitchen/cokitchen-components';
import { AddIcon } from 'cokitchen-icons';
import CurrencyInput from 'components/base/currency-input';
import LocalModal from 'components/base/local-modal';
import SelectDropdown from 'components/base/select-dropdown';
import useDashboardContext from 'contexts/dashboard.context';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import { formatCurrency, getFullName, handleApiError } from 'utils/functions';
type Props = {
  isOpen: boolean;
  onClose: () => void;
  setShowFundWalletModal: (showFundWalletModal: boolean) => void;
};

const GiftAMemberModal = ({ isOpen, onClose, setShowFundWalletModal }: Props) => {
  const { members, activeCompany, refetchMonthlyStat, companyWalletDetails } =
    useDashboardContext();
  const [member, setMember] = useState<{
    name: string;
    id: string;
  }>();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [insufficientBalance, setInsufficientBalance] = useState(false);
  const balance = companyWalletDetails?.available_balance;
  const [membersList, setMembersList] = useState([] as { name: string; id: string }[]);

  const giftMember = async () => {
    if (!activeCompany.id) return;
    setLoading(true);
    try {
      const res = await apiService.post('gift', {
        data: {
          user_id: member?.id,
          company_id: activeCompany.id,
          note,
          amount,
          gift_type: 'Other'
        }
      });
      if (res.status === 201) {
        toast(<ToastMessage type='success' message='Member gifted successfully' />);
        onClose();
        refetchMonthlyStat();
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const getMembers = () => {
    setMembersList(
      members.map((item) => {
        return { name: getFullName(item?.user), id: item.user_id };
      })
    );
  };

  useEffect(() => {
    if (members) {
      getMembers();
    }
  }, [members]);

  useEffect(() => {
    if (amount && +amount > Number(balance)) {
      setInsufficientBalance(true);
    } else {
      setInsufficientBalance(false);
    }
  }, [amount]);

  return (
    <LocalModal title='Gift a member' isOpen={isOpen} onRequestClose={onClose}>
      <div className='w-full relative'>
        <CurrencyInput
          label='How much would you like to gift'
          placeholder='10,000'
          value={amount}
          onChange={(v) => setAmount(v)}
          className='mb-4'
        />
      </div>
      <div className='w-full mt-4'>
        <SelectDropdown
          label='Select member'
          value={member?.name}
          setValue={(option) => setMember(option)}
          placeholder='Select a member'
          options={membersList}
        />
      </div>
      <div className='w-full relative mt-4'>
        <Textarea
          design='bordered'
          value={note}
          onChange={(e) => setNote(e.target.value)}
          label='Add a note (optional)'
          placeholder='e.g great job on the presentation yesterday '
          className='font-semibold'
        />
      </div>
      <div
        className={`w-full py-3 px-4 rounded-md border flex justify-between items-center mb-2 mt-8 ${
          insufficientBalance ? 'border-[#F2F2F3]' : 'border-[#E60A2B]'
        }`}>
        <div>
          <p className='text-sm text-[#2E3031] font-medium'>Company Wallet</p>
          <p className='text-[#6F7376] text-xs'>
            Balance: {balance ? formatCurrency(balance) : ''}
          </p>
        </div>
        {insufficientBalance ? (
          <div className='rounded-[100px] bg-[#F4F1F1] py-[3px] px-[10px] text-[#E60A2B] text-xs font-medium'>
            Insufficient
          </div>
        ) : (
          <div className='w-[14px] h-[14px] bg-[#E60A2B] p-[3.8px] flex justify-center items-center rounded-full'>
            <div className='w-full h-full bg-white rounded-full'></div>
          </div>
        )}
      </div>
      {insufficientBalance && (
        <div
          className='cursor-pointer flex items-center gap-[2px] text-[#0080E5]'
          onClick={() => setShowFundWalletModal(true)}>
          <p className=' text-xs font-medium'>Fund wallet</p>
          <AddIcon className='w-4 h-4' />
        </div>
      )}
      <div className='mt-10 flex justify-end'>
        <Button
          variant='block'
          color='primary'
          onClick={giftMember}
          className='w-[139px] h-[41px] text-sm'
          disabled={!amount || !member}
          loading={loading}>
          Confirm
        </Button>
      </div>
    </LocalModal>
  );
};

export default GiftAMemberModal;
